import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { CLICK_DECAY, clickStrength, getWaveLevels } from '../../../utils/soundWave';

import './SoundWave.scss';

const BARS = 5;

/**
 * A few bars that move with what the player is sounding: the band's spectrum, and a swell on each
 * count-in click, whose sound the analyser never hears. Drawn on its own frames through refs, so
 * the view around it doesn't re-render sixty times a second.
 *
 * @param {function} getSpectrum - The player's, giving its master's FFT in dB or null
 * @param {number} countInBeat - The click of the count-in sounding, 0 once it's done
 */
const SoundWave = ({ getSpectrum, countInBeat = 0, className }) => {
  const barsRef = useRef([]);
  const clickRef = useRef(0);

  useEffect(() => {
    clickRef.current = Math.max(clickRef.current, clickStrength(countInBeat));
  }, [countInBeat]);

  useEffect(() => {
    // Left as still bars where motion is turned down
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;

    let frame;
    const draw = () => {
      getWaveLevels(getSpectrum(), clickRef.current).forEach((level, i) => {
        if (barsRef.current[i]) barsRef.current[i].style.height = `${20 + level * 80}%`;
      });
      clickRef.current *= CLICK_DECAY;
      frame = requestAnimationFrame(draw);
    };

    draw();

    return () => cancelAnimationFrame(frame);
  }, [getSpectrum]);

  return (
    <span className={cx('SoundWave', className)} aria-hidden="true">
      {Array.from({ length: BARS }, (unused, i) => (
        <span
          key={i}
          className="wave-bar"
          ref={(node) => {
            barsRef.current[i] = node;
          }}
        />
      ))}
    </span>
  );
};

SoundWave.propTypes = {
  getSpectrum: PropTypes.func.isRequired,
  countInBeat: PropTypes.number,
  className: PropTypes.string,
};

export default SoundWave;
